import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  HostBinding
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';


export enum ToggleState {
  Init,
  Checked,
  Unchecked
}

export class ToggleChange {
  source: Toggle;
  checked: boolean;
}


@Component({
  selector: 'suka-toggle',
  template: `
    <label
      class="
        toggle
        toggle--distribution-{{distribution}}
      "
      [ngClass]="{
        'skeleton' : skeleton,
        'disabled': disabled
      }"
    >
      <ng-content></ng-content>
      <input
        type="checkbox"
        [id]="id"
        [value]="value"
        [name]="name"
        [required]="required"
        [checked]="checked"
        [disabled]="disabled"
        [attr.aria-label]="ariaLabel"
        [attr.aria-labelledby]="ariaLabelledby"
        [attr.aria-checked]="checked"
        (change)="onChange($event)"
        (click)="onClick($event)"
      />
      <span class="toggle-body">
        <span class="toggle-switch"></span>
        <span class="toggle-track">
          <span class="toggle-bg"></span>
          <span class="toggle-bg toggle-bg_negative"></span>
        </span>
      </span>
    </label>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: Toggle,
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
// tslint:disable-next-line: component-class-suffix
export class Toggle implements ControlValueAccessor {
  /**
   * Variable used for creating unique ids for toggle components.
   */
  static toggleCount = 0;

  /**
   * Set to `true` for toggle to be rendered with nested styles.
   */
  @Input() nested: boolean;
  /**
   * Set to `true` for toggle to be rendered without any classes on the host element.
   */
  @Input() inline = false;
  /**
   * Set to `true` for a disabled toggle.
   */
  @Input() disabled = false;
  /**
   * Set to `true` for a loading toggle.
   */
  @Input() skeleton = false;
  /**
   * Set to `true` to hide the toggle labels.
   */
  @Input() hideLabel = false;
  /**
   * Sets the name attribute on the `input` element.
   */
  @Input() name: string;
  /**
   * The unique id for the toggle component.
   */
  @Input() id = `toggle-${Toggle.toggleCount}`;
  /**
   * Reflects the required attribute of the `input` element.
   */
  @Input() required: boolean;
  /**
   * Sets the value attribute on the `input` element.
   */
  @Input() value: string;
  /**
   * Used to set the `aria-label` attribute on the input element.
   */
  // tslint:disable-next-line:no-input-rename
  @Input('aria-label') ariaLabel = '';
  /**
   * Used to set the `aria-labelledby` attribute on the input element.
   */
  // tslint:disable-next-line:no-input-rename
  @Input('aria-labelledby') ariaLabelledby: string;


  /**
   *  Horizontal distribution of elements
   */
  @Input() distribution: 'leading' | 'trailing' | 'equalSpacing' | 'center' = 'equalSpacing';

  /**
   * Returns value `true` if state is selected for the toggle.
   */
  get checked() {
    return this._checked;
  }

  /**
   * Updating the state of a toggle to match the state of the parameter passed in.
   */
  @Input() set checked(checked: boolean) {
    if (checked !== this.checked) {
      this._checked = checked;
      this.changeDetectorRef.markForCheck();
    }
  }

  @HostBinding('class.toggle-wrapper') get toggleWrapperClass() {
    return !this.inline;
  }
  @HostBinding('class.form-item') get formItemClass() {
    return !this.inline;
  }

  /**
   * Emits event notifying other classes when a change in state occurs on a toggle after a
   * click.
   */
  // tslint:disable-next-line: no-output-native
  @Output() change = new EventEmitter<ToggleChange>();

  /**
   * Set to `true` if the input toggle is selected (or checked).
   */
  // tslint:disable-next-line: variable-name
  _checked = false;

  currentToggleState: ToggleState = ToggleState.Init;

  /**
   * Maintains a reference to the view DOM element of the `Toggle`.
   */
  @ViewChild('inputToggle', { static: true }) inputToggle: ElementRef;

  /**
   * Creates an instance of `Toggle`.
   */
  constructor(protected changeDetectorRef: ChangeDetectorRef) {
    Toggle.toggleCount++;
  }

  /**
   * Toggle the selected state of the toggle.
   */
  public toggle() {
    this.checked = !this.checked;
  }

  // this is the initial value set to the component
  public writeValue(value: any) {
    this.checked = !!value;
  }

  /**
   * Sets a method in order to propagate changes back to the form.
   */
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * @param fn Callback to be triggered when the toggle is touched.
   */
  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Executes on the event of a change within `Toggle` to block propagation.
   */
  onChange(event) {
    event.stopPropagation();
  }

  /**
   * Handles click events on the `Toggle` and emits changes to other classes.
   */
  onClick(event) {
    if (!this.disabled) {
      this.toggle();
      this.transitionToggleState(this._checked ? ToggleState.Checked : ToggleState.Unchecked);
      this.emitChangeEvent();
    }
  }


  /**
   * Called when toggle is blurred. Needed to properly implement `ControlValueAccessor`.
   */
  onTouched: () => any = () => { };

  /**
   * Handles changes between toggle states.
   */
  transitionToggleState(newState: ToggleState) {
    const oldState = this.currentToggleState;

    if (oldState === newState) {
      return;
    }

    this.currentToggleState = newState;
  }

  /**
   * Creates instance of `ToggleChange` used to propagate the change event.
   */
  emitChangeEvent() {
    const event = new ToggleChange();
    event.source = this;
    event.checked = this.checked;

    this.propagateChange(this.checked);
    this.change.emit(event);
  }

  /**
   * Method set in `registerOnChange` to propagate changes back to the form.
   */
  propagateChange = (_: any) => { };
}
